import React, { useEffect, useMemo, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import Input from 'components/atoms/Input'
import styled from 'styled-components'
import { head, isEmpty } from 'ramda'
import PrimaryButton from 'components/atoms/PrimaryButton'
import { shareAnalyticsTemplateRoutine } from 'features/analytics/ducks/actions'
import { validateShareTemplateField, validateShareTemplateValues } from 'features/analytics/ducks/schema'
import { getBpConnectedUsers } from 'features/bpDetails/ducks/selectors'
import { clearConnectedUsersRoutine, getBpConnectedUsersByEmailRoutine } from 'features/bpDetails/ducks/actions'
import Modal from 'components/atoms/Modal'
import Toggle from 'components/atoms/Toggle'
import EditIcon from '@mui/icons-material/Edit'
import EditOffIcon from '@mui/icons-material/EditOff'
import { updateAnalyticsTemplateRoutine } from 'features/analytics/ducks/actions'
import { getCurrentUser } from 'features/auth/ducks/selectors'
import { toast } from 'react-toastify'

const ShareTemplateModal = ({ template, closeModal }) => {
  const connectedUsers = useSelector(getBpConnectedUsers)
  const dispatch = useDispatch()
  const currentUser = useSelector(getCurrentUser)
  const [searched, setSearched] = useState(false)
  const [email, setEmail] = useState('')
  const [valid, setValid] = useState(false)
  const [lockModalOpen, setLockModalOpen] = useState(false)
  const [locked, setLocked] = useState(false)
  const [pendingUser, setPendingUser] = useState(null)
  const creatorId = template?.creator_id
    || template?.creatorId
    || template?.creator?.id
    || template?.createdBy?.id
    || template?.created_by?.id
    || template?.userId
    || template?.user_id
    || template?.owner?.id
  const isCreator = currentUser?.id && creatorId && String(currentUser.id) === String(creatorId)
  const isAdmin = currentUser?.type === 'admin'
  const canManageLock = isCreator || isAdmin
  const normalizeLocked = value => value === true || value === 1 || value === '1'

  const userAlreadyShared = (templateData, userData) => {
    if (!templateData || !userData) return false

    if (
      userData.already_shared === true ||
      userData.alreadyShared === true ||
      userData.has_access === true ||
      userData.hasAccess === true
    ) {
      return true
    }

    const collections = [
      templateData.shared_users,
      templateData.sharedUsers,
      templateData.shared_with_users,
      templateData.sharedWithUsers,
      templateData.users
    ].filter(Array.isArray)

    return collections.some(list =>
      list.some(u =>
        String(u?.id || u?.user_id || u?.userId) === String(userData.id) ||
        String(u?.email || '').toLowerCase() === String(userData.email || '').toLowerCase()
      )
    )
  }

  const foundUser = useMemo(() => {
    return head(connectedUsers) || []
  }, [connectedUsers])

  const handleSearch = () => {
    dispatch(getBpConnectedUsersByEmailRoutine(email))
    setSearched(true)
  }

  const handleShareTemplate = () => {
    if (!foundUser?.id) return
    if (userAlreadyShared(template, foundUser)) {
      toast.info('This user already has access to this template.')
      return
    }
    setPendingUser(foundUser)
    setLocked(normalizeLocked(template?.locked ?? template?.is_locked ?? template?.isLocked ?? template?.pivot?.locked))
    setLockModalOpen(true)
  }

  const handleConfirmLockAndShare = () => {
    if (!pendingUser?.id) return
    const currentLocked = normalizeLocked(template?.locked ?? template?.is_locked ?? template?.isLocked ?? template?.pivot?.locked)
    if (canManageLock && locked !== currentLocked) {
      dispatch(updateAnalyticsTemplateRoutine({
        id: template.id,
        locked,
        silentSuccess: true
      }))
    }
    dispatch(shareAnalyticsTemplateRoutine({
      userId: pendingUser.id,
      templateId: template.id,
      source: 'share_template_modal'
    }))
    dispatch(clearConnectedUsersRoutine())
    setSearched(false)
    setLockModalOpen(false)
    setPendingUser(null)
    closeModal()
  }

  useEffect(() => {
    validateShareTemplateValues({ email }, setValid)
  }, [email])

  return (
    <FormContent>
      <InputsRow>
        <Input
          type='email'
          name='email'
          label='Email'
          value={email}
          onChange={(_, value) => setEmail(value)}
          validate={validateShareTemplateField({ email })}
        />
        <PrimaryButton
          disabled={!valid}
          onClick={handleSearch}
        >
          Search
        </PrimaryButton>
      </InputsRow>
        {
          !isEmpty(foundUser) && (
            <FoundUserDetails>
              <div>
                {foundUser.firstName} {foundUser.lastName}
              </div>
              <ShareButton onClick={handleShareTemplate}>share</ShareButton>
            </FoundUserDetails>
          )
        }
        {
          isEmpty(foundUser) && searched && (
            <div>There is no user connected with this email address</div>
          )
        }

      <Modal
        open={lockModalOpen}
        title="Set template editability"
        onClose={() => setLockModalOpen(false)}
        withSubmit
        withCancel
        submitButtonText="Share"
        cancelButtonText="Cancel"
        onSubmit={handleConfirmLockAndShare}
        onCancel={() => setLockModalOpen(false)}
      >
        <LockSection>
          <LockRow>
            <LockLabel>
              {locked ? <EditOffIcon fontSize="small" /> : <EditIcon fontSize="small" />}
              <span>{locked ? 'Non-editable' : 'Editable'}</span>
            </LockLabel>
            <Toggle
              checked={locked}
              onChange={() => setLocked(!locked)}
              disabled={!canManageLock}
            />
          </LockRow>
          <LockHint>
            {locked
              ? 'Shared users will not be able to edit this template.'
              : 'Shared users can edit this template.'}
          </LockHint>
          {!canManageLock && (
            <LockNote>Only the template creator can change lock status.</LockNote>
          )}
        </LockSection>
      </Modal>
    </FormContent>
  )
}

export default ShareTemplateModal

const InputsRow = styled.div`
  display: flex;
  align-items: center;

  & > div:first-of-type {
    margin-right: 15px;
  }

  button {
    height: 45px;
  }
`

const FormContent = styled.div`

`

const FoundUserDetails = styled.div`
  display: flex;
  justify-content: space-between;
  margin: 10px 0;
  align-items: center;
  padding: 5px 10px;
  border: 1px solid ${({ theme }) => theme.colors.grey[500]};
  border-radius: 4px;
`

const ShareButton = styled.div`
  text-transform: uppercase;
  color: ${({ theme }) => theme.colors.secondary};
  font-size: 12px;
  font-weight: bold;
  width: fit-content;
  cursor: pointer;
`

const LockSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`

const LockRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
`

const LockLabel = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.haiti};
`

const LockHint = styled.div`
  font-size: 12px;
  color: ${({ theme }) => theme.colors.grey[700]};
`

const LockNote = styled.div`
  font-size: 12px;
  color: ${({ theme }) => theme.colors.grey[600]};
`
